import type { ComponentProps, ReactNode } from 'react';
import { cn } from './cn';

const control =
  'block w-full rounded-lg border bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 disabled:bg-slate-100 disabled:text-slate-500';

function stateClass(invalid?: boolean) {
  return invalid ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-300';
}

export interface InputProps extends ComponentProps<'input'> {
  invalid?: boolean;
  leading?: ReactNode;
}

export function Input({ invalid, leading, className, ...props }: InputProps) {
  if (leading) {
    return (
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
          {leading}
        </span>
        <input
          aria-invalid={invalid || undefined}
          className={cn(control, 'h-10 pl-10', stateClass(invalid), className)}
          {...props}
        />
      </div>
    );
  }
  return (
    <input
      aria-invalid={invalid || undefined}
      className={cn(control, 'h-10', stateClass(invalid), className)}
      {...props}
    />
  );
}

export function Textarea({ invalid, className, ...props }: ComponentProps<'textarea'> & { invalid?: boolean }) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={cn(control, 'min-h-24 py-2', stateClass(invalid), className)}
      {...props}
    />
  );
}

export function Select({ invalid, className, children, ...props }: ComponentProps<'select'> & { invalid?: boolean }) {
  return (
    <select
      aria-invalid={invalid || undefined}
      className={cn(control, 'h-10 pr-8', stateClass(invalid), className)}
      {...props}
    >
      {children}
    </select>
  );
}

export function Checkbox({ className, ...props }: ComponentProps<'input'>) {
  return (
    <input
      type="checkbox"
      className={cn('h-4 w-4 shrink-0 rounded border-slate-300 accent-brand-500 cursor-pointer', className)}
      {...props}
    />
  );
}

export interface FormFieldProps {
  label: ReactNode;
  htmlFor?: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function FormField({ label, htmlFor, error, hint, required, className, children }: FormFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-brand-600">*</span>}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-xs font-medium text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}
